/**
 * TextTool.tsx
 * 
 * 职责：
 * 1. 提供文本工具的用户界面组件
 * 2. 管理文本颜色选择功能
 * 3. 与TextService服务交互，更新文本绘制属性
 * 4. 提供用户友好的文本工具面板
 * 5. 条件渲染工具面板，仅在文本工具激活时显示
 * 6. 维护本地状态与TextService状态的同步
 * 7. 提供文本输入相关的用户指引
 */

import React, { useState, useEffect } from 'react';
import { TextService } from '../services/TextService';

interface TextToolProps {
  textService: TextService;
  isActive: boolean;
  position?: { top: number, left: number };
}

const TextTool: React.FC<TextToolProps> = ({
  textService,
  isActive,
  position
}) => {
  // 使用React状态来跟踪当前选择的文本属性
  const [currentColor, setCurrentColor] = useState<string>(textService.getColor());
  // 添加动画状态
  const [isVisible, setIsVisible] = useState(false);

  // 当组件挂载时同步初始状态
  useEffect(() => {
    setCurrentColor(textService.getColor());
  }, [textService]);
  
  // 监听isActive变化，添加延迟显示效果
  useEffect(() => {
    if (isActive) {
      // 延迟一帧显示，确保CSS过渡生效
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 10);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [isActive]);

  // 防止事件冒泡，阻止点击工具面板选项时自动切换回选择工具
  const handlePanelClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // 触发自定义事件
    document.dispatchEvent(new CustomEvent('tool-panel-interaction'));
  };

  // 处理颜色变更
  const handleColorChange = (color: string) => {
    textService.setColor(color);
    setCurrentColor(color); // 同时更新本地状态
    // 触发自定义事件
    document.dispatchEvent(new CustomEvent('tool-panel-interaction'));
  };

  // 如果工具未激活，不显示面板
  if (!isActive) {
    return null;
  }
  
  // 计算面板位置
  const panelStyle = position ? {
    top: `${position.top}px`,
    left: `${position.left}px`,
  } : {
    top: '60px',
    left: '80px',
  };

  return (
    <div className="text-tool-panel" style={{
      position: 'absolute',
      ...panelStyle,
      backgroundColor: '#f5f5f5',
      border: '1px solid #ccc',
      borderRadius: '4px',
      padding: '10px',
      boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
      zIndex: 9000,
      opacity: isVisible ? 1 : 0,
      transform: isVisible ? 'translateY(0)' : 'translateY(-10px)',
      transition: 'opacity 0.3s ease, transform 0.3s ease',
      userSelect: 'none',
      WebkitUserSelect: 'none',
      MozUserSelect: 'none',
      msUserSelect: 'none',
      minWidth: '200px'
    }}
    onClick={handlePanelClick}
    >
      <h4 style={{ marginBottom: '8px', fontSize: '14px', color: '#333' }}>文本工具</h4>
      
      <div className="text-instructions">
        <p style={{ marginBottom: '8px', fontSize: '12px' }}>点击画布并输入文本</p>
        
        {/* 颜色选择器 */}
        <div style={{ marginBottom: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '5px' }}>
            <span style={{ fontSize: '12px', marginRight: '5px' }}>颜色:</span>
            <div 
              style={{ 
                width: '20px', 
                height: '20px', 
                backgroundColor: currentColor,
                border: '1px solid #ccc',
                borderRadius: '3px'
              }} 
            />
          </div>
          
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
            {['#000000', '#FF0000', '#0000FF', '#00FF00', '#FFFF00'].map(color => (
              <div
                key={color}
                onClick={() => handleColorChange(color)}
                style={{
                  width: '20px',
                  height: '20px',
                  backgroundColor: color,
                  border: `2px solid ${currentColor === color ? 'blue' : 'transparent'}`,
                  borderRadius: '50%',
                  cursor: 'pointer',
                }}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TextTool; 